import React from 'react';

// Expiry indicator for a pen, driven by the DOS-7 fields on GET /api/pens.
// Three states for an active pen (all measured from the FIRST injection):
//   - past window   (daysUntilExpiration <= 0): amber "Past window" safety caution (DOS-25)
//   - near expiry    (0 < days < NEAR_EXPIRY_DAYS): orange "Expires in N days" (DOS-12)
//   - upcoming       (days >= NEAR_EXPIRY_DAYS): neutral "Expires in N days" (DOS-12)
// Naturally-expired pens (status 'expired') and pens with no injections show nothing.
interface PenExpiryWarningProps {
  status?: string;
  daysUntilExpiration?: number | null;
  expirationDays?: number;
  expiresAt?: string | null;
  variant?: 'chip' | 'inline';
}

export const NEAR_EXPIRY_DAYS = 7;

export function isPenPastWindow(status?: string, daysUntilExpiration?: number | null): boolean {
  // <= 0 matches the backend's shouldExpire rule (expiresAt <= now). A pen that
  // expired within the last day reports daysUntilExpiration === 0, so `< 0`
  // would miss it — it auto-expires but shows no caution once restored to active.
  return status === 'active' && typeof daysUntilExpiration === 'number' && daysUntilExpiration <= 0;
}

// Whether an active pen has a countdown to show (has a first injection, not yet past window).
export function hasExpiryCountdown(status?: string, daysUntilExpiration?: number | null): boolean {
  return status === 'active' && typeof daysUntilExpiration === 'number' && daysUntilExpiration > 0;
}

function pastWindowMessage(daysUntilExpiration: number, expirationDays?: number): string {
  const daysOver = Math.abs(daysUntilExpiration);
  const windowText = expirationDays ? ` ${expirationDays}-day` : '';
  const overText = daysOver >= 1 ? `${daysOver} day${daysOver !== 1 ? 's' : ''} past` : 'past';
  return `This pen is ${overText} the recommended${windowText} in-use window. Check the medication before dosing.`;
}

function countdownTooltip(days: number, expirationDays?: number, expiresAt?: string | null): string {
  const basis = expirationDays ? ` — ${expirationDays} days after first dose` : '';
  if (expiresAt) {
    const d = new Date(expiresAt);
    if (!isNaN(d.getTime())) {
      return `Expires ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}${basis}`;
    }
  }
  return `Expires in ${days} day${days !== 1 ? 's' : ''}${basis}`;
}

const PenExpiryWarning: React.FC<PenExpiryWarningProps> = ({
  status,
  daysUntilExpiration,
  expirationDays,
  expiresAt,
  variant = 'chip',
}) => {
  const pastWindow = isPenPastWindow(status, daysUntilExpiration);

  // Inline (dose-confirmation dialog): only the past-window safety caution, to keep
  // the confirm step focused. The countdown lives on the pen cards.
  if (variant === 'inline') {
    if (!pastWindow) return null;
    return (
      <div
        className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
        role="alert"
        data-testid="pen-expiry-warning"
      >
        <span aria-hidden="true">⚠️</span>
        <span>{pastWindowMessage(daysUntilExpiration as number, expirationDays)}</span>
      </div>
    );
  }

  // Chip (pen card)
  if (pastWindow) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border border-amber-300 bg-amber-100 text-amber-800 whitespace-nowrap"
        title={pastWindowMessage(daysUntilExpiration as number, expirationDays)}
        role="alert"
        data-testid="pen-expiry-warning"
      >
        <span aria-hidden="true">⚠️</span>
        Past window
      </span>
    );
  }

  if (hasExpiryCountdown(status, daysUntilExpiration)) {
    const days = daysUntilExpiration as number;
    const nearExpiry = days < NEAR_EXPIRY_DAYS;
    const style = nearExpiry
      ? 'border-orange-300 bg-orange-100 text-orange-800'
      : 'border-stone-200 bg-stone-100 text-stone-600';
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${style}`}
        title={countdownTooltip(days, expirationDays, expiresAt)}
        data-testid="pen-expiry-countdown"
      >
        {nearExpiry && <span aria-hidden="true">⚠️</span>}
        Expires in {days} day{days !== 1 ? 's' : ''}
      </span>
    );
  }

  return null;
};

export default PenExpiryWarning;
